import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react'
import { useMedia } from 'react-use'
import { Drawer, VisuallyHidden } from '@overdose/components'
import {
  IconArrowLeft,
  IconArrowRight,
  IconChevronRight,
  IconX,
} from '@tabler/icons-react'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { SiteNavigationItem } from 'shared-types'
import { Button, Link } from '~/components'
import { PromoCard, PromoCardProps } from '~/components/PromoCard'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'

import { useAlgoliaSearchInsignts, usePage } from '~/hooks'
import { DEFAULT_COUNTRY_CODE_NZ } from '~/constants/countries'
import styles from './MegaMenu.module.css'
import { MegaMenuProps } from './MegaMenu.types'

const DEPTH_2 = 2
const DEPTH_3 = 3

export const MegaMenu = ({
  isOpen,
  pages,
  categories,
  onClose,
}: MegaMenuProps) => {
  const [selectedMenuIds, setSelectedMenuIds] = useState<string[]>([])

  const page = usePage()
  const isNzSite = page.countryCode === DEFAULT_COUNTRY_CODE_NZ
  const { sendViewProductsEvent } = useAlgoliaSearchInsignts()
  const isRTM = page.brand === 'rtm'
  const isMobile = useMedia('(max-width: 1023px)', false)
  const router = useRouter()

  const contentStyle = 'w-full h-full lg:overflow-x-hidden overflow-y-auto'
  const innerStyle = classNames(styles.rootPanel, 'px-4 py-8 lg:p-12')
  const hiddenStyle = 'lg:hidden'
  const megaMenuMobileClassName = '.megaMenuMobile'

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const headerFormEl = page.getHeaderFormEl()
    const { top } = headerFormEl.getBoundingClientRect()
    const { height } = headerFormEl.getBoundingClientRect()

    const mobileMegaMenu = document.getElementsByClassName(
      'megaMenuMobile'
    )?.[0] as HTMLDivElement

    if (mobileMegaMenu?.style) {
      mobileMegaMenu.style.setProperty('--drawer-top', `${height + top - 1}px`)
    }
  }, [page, isOpen])

  const handleCloseDrawer = useCallback(() => {
    setSelectedMenuIds([])
    onClose?.()
  }, [setSelectedMenuIds, onClose])

  useEffect(() => {
    const keydowHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCloseDrawer()
      }
    }

    window.addEventListener('keydown', keydowHandler)

    return () => {
      window.removeEventListener('keydown', keydowHandler)
    }
  }, [handleCloseDrawer])

  useEffect(() => {
    if (isOpen && isMobile) {
      document.documentElement.style.setProperty(
        '--drawer-top',
        `${page.pageHeaderHeight}px`
      )
    } else {
      document.documentElement.style.removeProperty('--drawer-top')
    }
  }, [isOpen, isMobile])

  const handleSelectMenu = (depth: number, menuId: string) => {
    const selected = selectedMenuIds.slice(0, depth)
    setSelectedMenuIds([...selected, menuId])

    document
      .querySelector(megaMenuMobileClassName)
      ?.classList?.add(styles.mobileInnerPannel)
    isNzSite &&
      document
        .querySelector(megaMenuMobileClassName)
        ?.classList?.add(styles.mobileInnerPannel_nz)
  }

  const handlePrevious = (depth: number) => {
    if (depth === 0) {
      document
        .querySelector(megaMenuMobileClassName)
        ?.classList?.remove(styles.mobileInnerPannel)

      isNzSite &&
        document
          .querySelector(megaMenuMobileClassName)
          ?.classList?.remove(styles.mobileInnerPannel_nz)
    }

    setSelectedMenuIds([...selectedMenuIds.slice(0, depth)])
  }

  const renderBackBtn = (depth: number, isHideBackBtn = true) => {
    return (
      <Button
        theme={{
          root: classNames(styles.iconBtn, hiddenStyle, {
            [styles.stack2Back]: depth === DEPTH_2,
            [styles.stack3Back]: depth === DEPTH_3,
            [styles.stack2BackPromo]: depth === DEPTH_2 && isHideBackBtn,
          }),
        }}
        size='sm'
        variant='ghost'
        style={{
          '--btn-ghost-text-decoration': 'none',
          '--btn-size-sm-height': 'max-content',
        }}
        icon={<IconArrowLeft />}
        iconOnly
        onClick={() => {
          return handlePrevious(depth - 1)
        }}
      />
    )
  }

  const renderCategoryTitle = (depth: number, url: string, title: string) => {
    return (
      <div
        className={classNames('text-primary-blue py-6 lg:pt-10 lg:pb-4', {
          [styles.stack2Title]: depth === DEPTH_2,
          [styles.stack3Title]: depth === DEPTH_3,
        })}>
        <Link
          href={url}
          onClick={() => {
            return handleCloseDrawer()
          }}>
          <Typography
            tag={TypographyTag.p}
            variant={TypographyVariant.BodyLargeBold}>
            {title}
          </Typography>
        </Link>
      </div>
    )
  }

  const renderShopAllLink = (
    url: string,
    title: string,
    categorySku: string
  ) => {
    return (
      <li className='border-b border-greyscale-300'>
        {url && (
          <Link
            href={url}
            onClick={() => {
              sendViewProductsEvent([
                {
                  title,
                  url,
                  listId: categorySku.replace(/^0+/, ''),
                },
              ])
              return handleCloseDrawer()
            }}
            className='flex justify-between px-0 py-3 lg:py-4 !bg-transparent !border-0 hover:underline text-primary-heading'>
            <Typography
              tag={TypographyTag.p}
              variant={TypographyVariant.BodyRegularBold}
              className='no-underline text-primary-body'>
              Shop all {title}
            </Typography>
            <IconArrowRight size={20} />
          </Link>
        )}
      </li>
    )
  }

  const renderChildLink = (
    li: SiteNavigationItem,
    index: number,
    depth: number
  ) => {
    const hasChildren = !!li.children?.length
    const isLink = !hasChildren && !!li.url
    const linkHref = hasChildren ? null : li.url
    const isCurrentAndLinkPathSame = linkHref === router.asPath

    return (
      <li
        key={index}
        className={classNames(
          'border-b',
          depth === 0 ? styles.listBorderPrimary : 'border-primary-border'
        )}>
        <Button
          href={isCurrentAndLinkPathSame ? null : linkHref}
          theme={{
            root: classNames(
              isLink && 'hover:underline',
              'max-md:!text-xs w-full justify-between p-0 my-1 lg:my-2 !border-0 !bg-transparent',
              depth === 0
                ? styles.megaMenuLinkPrimary
                : styles.megaMenuLinkSecondary,
              {
                '!font-bold !text-white':
                  selectedMenuIds.includes(li.categoryId) && depth === 0,
                '!border-l-4 pl-4 !border-solid !border-text-action-link-hover !font-bold !rounded-none':
                  selectedMenuIds.includes(li.categoryId) && isRTM,
                '!text-primary-body':
                  selectedMenuIds.includes(li.categoryId) && depth !== 0,
              }
            ),
          }}
          variant='ghost'
          style={{
            '--btn-ghost-text-decoration': 'none',
            '--btn-font-weight': 400,
            '--btn-icon-size': '20px',
            '--btn-size-md-height': '32px',
            '--btn-ghost-color-text-primary-hover':
              depth === 0
                ? 'var(--color-grayscale-white)'
                : 'var(--color-typography-body)',
          }}
          onClick={() => {
            depth > 0 &&
              !hasChildren &&
              sendViewProductsEvent([
                {
                  title: li.title,
                  url: li.url,
                  listId: li.categorySku,
                },
              ])
            return !isLink && handleSelectMenu(depth, li.categoryId)
          }}
          icon={
            !isLink && (
              <IconChevronRight
                strokeWidth={2}
                stroke={
                  depth === 0
                    ? 'var(--mega-menu-color-link-primary)'
                    : 'var(--mega-menu-color-link-secondary)'
                }
                className={classNames({
                  '!stroke-white':
                    selectedMenuIds.includes(li.categoryId) && depth === 0,
                })}
              />
            )
          }
          iconAlignment='right'>
          {li.title}
        </Button>
      </li>
    )
  }

  const renderPromoProduct = (depth: number, promoProduct: PromoCardProps) => {
    return (
      <div className={contentStyle}>
        <div className={innerStyle}>
          {renderBackBtn(depth, true)}
          <div className={classNames(styles.cardWrap, '!pt-3')}>
            <PromoCard {...promoProduct} />
          </div>
        </div>
      </div>
    )
  }

  const renderMenu = (depth: number, menu?: SiteNavigationItem) => {
    if (!menu) {
      return null
    }

    const { title, url, children, categorySku } = menu
    return (
      <div className={contentStyle}>
        <div className={depth > 0 ? innerStyle : ''}>
          {depth > 0 && renderBackBtn(depth)}
          {!!title && renderCategoryTitle(depth, url, title)}
          <ul>
            {!!url && renderShopAllLink(url, title, categorySku)}
            {children?.map((li, index) => {
              return renderChildLink(li, index, depth)
            })}
          </ul>
        </div>
      </div>
    )
  }

  const renderPanel = (depth: number, menu?: SiteNavigationItem) => {
    if (!menu) {
      return null
    }

    const { children } = menu

    return (
      <>
        {renderMenu(depth, menu)}
        {children?.map((li, index) => {
          const hasChildren = li.children?.length > 0
          const hasPromoProduct = !!li.promoProduct
          const childDepth = depth + 1
          const isActive = li.categoryId === selectedMenuIds[depth]
          const panelStyle = [
            'absolute top-0 h-full bg-white',
            styles.panel,
            isActive ? styles.active : '',
          ]

          if (!hasChildren) {
            return null
          }

          return (
            <Fragment key={index}>
              <div
                className={classNames(panelStyle, {
                  [styles.stack2]: childDepth === DEPTH_2,
                  [styles.stack3]: childDepth === DEPTH_3,
                  [styles.stack2Shift]:
                    childDepth === DEPTH_2 && hasPromoProduct,
                })}>
                {renderPanel(childDepth, li)}
              </div>
              {childDepth < DEPTH_3 && hasPromoProduct && (
                <div
                  className={classNames(panelStyle, styles.stackPromo3, {
                    '!hidden': childDepth !== selectedMenuIds.length,
                    [styles.stack2ForwardPromo]: childDepth === DEPTH_2,
                    [styles.stack2]: childDepth === DEPTH_2,
                    [styles.stack3]: childDepth === DEPTH_3,
                  })}>
                  {renderPromoProduct(childDepth, li.promoProduct)}
                </div>
              )}
            </Fragment>
          )
        })}
      </>
    )
  }

  const renderPageLinks = useCallback(() => {
    return (
      <div className={classNames(styles.pageLinks, 'pt-8')}>
        {pages?.map((page, index) => {
          return (
            <Link
              key={index}
              href={page.href}
              onClick={() => {
                return handleCloseDrawer()
              }}>
              <Typography
                tag={TypographyTag.p}
                variant={TypographyVariant.BodyRegularMedium}
                className={classNames('no-underline', styles.pageLink)}>
                {page.title}
              </Typography>
              <VisuallyHidden>{page.title}</VisuallyHidden>
            </Link>
          )
        })}
      </div>
    )
  }, [handleCloseDrawer, pages])

  useLayoutEffect(() => {
    if (isOpen) {
      setSelectedMenuIds([])
    }
  }, [isOpen])

  return (
    <>
      <Drawer
        theme={{
          wrapper: 'hidden lg:block overflow-y-auto',
          inner: 'overflow-visible',
          header: 'hidden',
          content: 'p-0',
          mask: 'backdrop-blur-md',
        }}
        visible={isOpen}
        placement='left'
        onCancel={handleCloseDrawer}>
        <div
          className={classNames(
            styles.rootPanel,
            'flex flex-col relative min-h-full p-12 text-white bg-background-primary-muted'
          )}>
          <Button
            style={{
              '--btn-icon-size': '32px',
              '--btn-size-md-height': '32px',
            }}
            onClick={handleCloseDrawer}
            iconOnly
            icon={<IconX />}
          />
          <div className='py-4'>
            <Typography
              tag={TypographyTag.p}
              variant={TypographyVariant.Heading5}
              className='!text-white'>
              Products
            </Typography>
          </div>
          <div className='flex-1'>
            {renderPanel(0, {
              categoryId: '',
              categorySku: '',
              title: '',
              url: '',
              children: categories,
            })}
          </div>
          {renderPageLinks()}
        </div>
      </Drawer>
      {isMobile && (
        <Drawer
          theme={{
            wrapper: classNames(
              `${styles.mobileDrawerWrapper} lg:hidden megaMenuMobile`
            ),
            root: classNames(styles.mobileDrawer),
            header: 'hidden',
            content: 'p-0',
            mask: 'bg-transparent',
          }}
          bodyStye={{
            backgroundColor: '#000',
          }}
          visible={isOpen}
          placement='left'
          onCancel={() => {
            return onClose?.()
          }}>
          <div className='relative h-full overflow-hidden bg-background-primary-muted'>
            <div className='flex flex-col px-4 py-6 h-full overflow-y-auto'>
              <div className='pb-4'>
                <Typography
                  tag={TypographyTag.p}
                  variant={TypographyVariant.Heading5}
                  className='!text-white'>
                  Products
                </Typography>
              </div>
              <div className='flex-1'>
                {renderPanel(0, {
                  categoryId: '',
                  categorySku: '',
                  title: '',
                  url: '',
                  children: categories,
                })}
              </div>
              {renderPageLinks()}
            </div>
          </div>
        </Drawer>
      )}
    </>
  )
}
